// ./src/utils/api/AccountPreferenceService.ts
//
// The account's EVENT DEFAULTS (ROADMAP A2b #53) — what a new event starts from.
//
// ⚠⚠ ONE ROW PER ACCOUNT, SO THERE IS NO LIST AND NO id. The account IS the key: `get()` returns the
// signed-in account's row (an empty one when they have never saved), and `save()` upserts a PARTIAL
// patch. A field left out of the patch is untouched; a field sent as null clears that default.
//
// ⚠ THIS REPLACES `UserProfileService`, WHICH QUERIED A SCHEMA THIS PRODUCT HAS NEVER HAD.
// `userProfileList { homeCurrency }` is defined in exactly one repo in this org and it is
// **carexpenses-api**, which is not one of the federation gateway's subgraphs — the same shape as the
// notifications bell deleted in #51. The gateway answered every call with
// `Cannot query field "userProfileList" on type "Query"`, so the old Preferences page showed the
// hard-coded default forever and its Save could never succeed. Nothing failed loudly; a rejected
// query looks exactly like an empty profile.
import { gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';

import { ApiService } from './ApiService';

const FIELDS = `
  id
  accountId
  defaultCurrencyCode
  defaultVenueId
  defaultThemeId
  defaultTimezone
  defaultEventFormat
  defaultEventMode
  defaultGuestTracking
  viableFillRate
  viableCostCoverage
  status
  createdAt
  updatedAt
`;

const GET_QUERY = gql`
  query AccountPreferenceGet {
    accountPreferenceGet {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const SAVE_MUTATION = gql`
  mutation AccountPreferenceSave($params: AccountPreferenceInput) {
    accountPreferenceSave(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

/**
 * A2e #97 — the account's custom CSS (every event website) has its OWN read and save. ⚠ Deliberately not in FIELDS:
 * those are read app-wide (the wizard's defaults), and a gateway not yet recomposed rejects a WHOLE query naming an
 * unknown field — the new field must cost the CSS drawer, never the wizard.
 */
const SITE_CSS_QUERY = gql`
  query AccountPreferenceSiteCss {
    accountPreferenceGet {
      code
      data { id siteCustomCss }
      errors { name errors warnings }
    }
  }
`;

const SITE_CSS_SAVE = gql`
  mutation AccountPreferenceSiteCssSave($params: AccountPreferenceInput) {
    accountPreferenceSave(params: $params) {
      code
      data { id siteCustomCss }
      errors { name errors warnings }
    }
  }
`;

/**
 * ROADMAP #190 — the account's DEFAULT dashboard cards (every event without its own list). ⚠ Its own read and save, like
 * the CSS above: an un-recomposed gateway must cost the dashboard drawer, never the wizard.
 */
const DASHBOARD_CARDS_QUERY = gql`
  query AccountPreferenceDashboardCards {
    accountPreferenceGet {
      code
      data { id dashboardCards { id hidden } }
      errors { name errors warnings }
    }
  }
`;

const DASHBOARD_CARDS_SAVE = gql`
  mutation AccountPreferenceDashboardCardsSave($params: AccountPreferenceInput) {
    accountPreferenceSave(params: $params) {
      code
      data { id dashboardCards { id hidden } }
      errors { name errors warnings }
    }
  }
`;

/**
 * #204 — gifts and memberships on the organizer page take TEST cards. Its own read and save, like the CSS above: an
 * un-recomposed gateway must cost the test-mode card, never the wizard.
 */
const SALES_TEST_MODE_QUERY = gql`
  query AccountPreferenceSalesTestMode {
    accountPreferenceGet {
      code
      data { id salesTestMode }
      errors { name errors warnings }
    }
  }
`;

const SALES_TEST_MODE_SAVE = gql`
  mutation AccountPreferenceSalesTestModeSave($params: AccountPreferenceInput) {
    accountPreferenceSave(params: $params) {
      code
      data { id salesTestMode }
      errors { name errors warnings }
    }
  }
`;

export interface AccountPreference {
  id?: string;
  accountId?: string | null;
  /** ISO 4217 a NEW event starts in. NOT the billing currency, which locks after the first payment. */
  defaultCurrencyCode?: string | null;
  /** Resolved live by the server — a removed venue comes back as null. */
  defaultVenueId?: string | null;
  /** B20 round 2 — the theme a new event / organizer page starts with. Resolved live by the server (a removed one → null). */
  defaultThemeId?: string | null;
  defaultTimezone?: string | null;
  defaultEventFormat?: string | null;
  defaultEventMode?: string | null;
  defaultGuestTracking?: string | null;
  /** 0.01–0.99. Below this share of capacity, Home calls a registration at risk. */
  viableFillRate?: number | null;
  /**
   * TICKETED events: the share of PROJECTED COST that projected revenue must reach (1 = break even).
   * ⚠ Not an ROI. ROI is net/cost, so "below 100%" as an ROI is every event that does not double its
   * money — it would flag a comfortably profitable event every time.
   */
  viableCostCoverage?: number | null;
  /** A2e #97 — custom CSS added to every event website of the account (read / saved on its own — see SITE_CSS_QUERY). */
  siteCustomCss?: string | null;
  /** ROADMAP #190 — the default dashboard cards (read / saved on their own — see DASHBOARD_CARDS_QUERY). Null: built-in. */
  dashboardCards?: { id: string; hidden: boolean }[] | null;
  /** #204 — gifts and memberships on the organizer page take TEST cards (read / saved on their own). */
  salesTestMode?: boolean | null;
  status?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** A PARTIAL patch — omit a field to leave it alone, send null to clear it. */
export type AccountPreferenceInput = Omit<AccountPreference, 'id' | 'accountId' | 'status' | 'createdAt' | 'updatedAt'>;

export class AccountPreferenceService {
  static async get(): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, {});
  }

  static async save(params: AccountPreferenceInput): Promise<OpResult> {
    return ApiService.executeMutation(SAVE_MUTATION, { params });
  }

  /** A2e #97 — the account's custom CSS for every event website. */
  static async getSiteCss(): Promise<OpResult> {
    return ApiService.executeQuery(SITE_CSS_QUERY, {}, { fetchPolicy: "network-only" });
  }

  /** A2e #97 — saves ONLY the custom CSS (a partial patch); null clears it. */
  static async saveSiteCss(siteCustomCss: string | null): Promise<OpResult> {
    return ApiService.executeMutation(SITE_CSS_SAVE, { params: { siteCustomCss } });
  }

  /** ROADMAP #190 — the account's default dashboard cards (fresh: the drawer edits them). */
  static async getDashboardCards(): Promise<OpResult> {
    return ApiService.executeQuery(DASHBOARD_CARDS_QUERY, {}, { fetchPolicy: "network-only" });
  }

  /** #204 — whether gifts and memberships on the organizer page take test cards (fresh: a switch shows it). */
  static async getSalesTestMode(): Promise<OpResult> {
    return ApiService.executeQuery(SALES_TEST_MODE_QUERY, {}, { fetchPolicy: "network-only" });
  }

  /** #204 — saves ONLY the sales test mode (a partial patch). */
  static async saveSalesTestMode(salesTestMode: boolean): Promise<OpResult> {
    return ApiService.executeMutation(SALES_TEST_MODE_SAVE, { params: { salesTestMode } });
  }

  /** ROADMAP #190 — saves ONLY the default dashboard cards (a partial patch); null clears them (the built-in order). */
  static async saveDashboardCards(dashboardCards: { id: string; hidden: boolean }[] | null): Promise<OpResult> {
    return ApiService.executeMutation(DASHBOARD_CARDS_SAVE, { params: { dashboardCards } });
  }
}
